import React, { ReactElement, RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { FormProps, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Select } from 'common/components/antd/Select/Select';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE, IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import {
  useAddPermissionToBranchRoleMutation,
  useDeleteRoleInBranchRoleMutation,
  useGetBranchesMultiRoleQuery_MultiRole,
  useGetPermissionTableByBranchQuery,
  useRemovePermissionToBranchRoleMutation,
} from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData,
} from 'domains/branches/types/branch.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import {
  TCounterpartiesListReqParams,
} from 'domains/counterparties/types/counterparty.types';
import {
  TPermission,
  TPermissionTableResData,
  TRow,
  TRolesListResData,
  TScopeResData,
} from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SContainer,
  SControlsContainer,
  SScrollNavWrapper,
	SInnerFiltersFormWrapper,
} from './branch-roles.page.styles';
import { ScrollerComponent } from './components/scroller/scroller.component';
import {
	EUserManagementBranchRolesPageGetParams,
	EUserManagementBranchRolesPageGetParamsActionTypes,
} from './enums/branch-roles.enum';
import CreateRoleModal from './modals/create-role.modal';
import PermissionTable, { TPermissionTableProps } from './table/permission.table';
import { KEY_SEPARATOR } from './table/table.constants';
import { ERoleMenuItemKey } from './table/table.enums';
import { TPayload, TScopeRecord, TTableColumn } from './table/table.types';
import { TAccountManagementBranchRolesPageLoaderData, TCreateRoleModalData } from './types/branch-roles.types';

enum EFiltersForm {
	Counterparty = 'counterparty',
	Branch = 'branch',
}

type TCheckBoxStatus = {
  key: string,
  value: boolean,
}

const UserRoleManagementBranchRolesPage = (): ReactElement => {
  const searchParams = new URLSearchParams(document.location.search);
  const { t } = useTranslation();
  const [permissionTableForm] = Form.useForm();
  const [filtersForm] = Form.useForm();
  const navigate = useNavigate();
  const loaderData = useLoaderData() as TAccountManagementBranchRolesPageLoaderData;
  const [selectedCounterparty, setSelectedCounterparty] = useState<string>(ENV_COUNTERPARTY_GUID);
  const [permissionTableFilters, setPermissionTableFilters] = useState<Record<EFiltersForm, string>>({
    counterparty: loaderData.defaultCounterparty,
    branch: loaderData.defaultBranch,
  });
  const scrollableTableNodeRef = useRef<Element>();
  const [modalData, setModalData] = useState<TCreateRoleModalData>({});
  const [field, setField] = useState<TCheckBoxStatus>({
    key: '',
    value: false,
  });

  const {
    data: permissionTableData,
    isLoading: isPermissionTableDataLoading,
    isError: isPermissionTableDataError,
    error: permissionTableDataError,
  } = useGetPermissionTableByBranchQuery(
    permissionTableFilters.counterparty,
    permissionTableFilters.branch,
    {},
    {
      refetchOnMount: false,
    },
  );

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
      enabled: IS_ADMIN_FE,
    },
  );

  const {
    data: branchesData,
    isLoading: isBranchesDataLoading,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(selectedCounterparty, {}, loaderData.hasAccessForAllBranches, {
    refetchOnMount: false,
    select: (data: any) => {
      const redoData = {
        ...data,
        ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
      };

      return redoData;
    },
  });

  const isError = isPermissionTableDataError || isCounterpartiesDataError || isBranchesDataError;
  const errorData = permissionTableDataError || counterpartiesDataError || branchesDataError;

  const { mutateAsync: addPermissionToBranchRoleMutation } = useAddPermissionToBranchRoleMutation();
  const { mutateAsync: removePermissionToBranchRoleMutation } = useRemovePermissionToBranchRoleMutation();
  const { mutateAsync: deleteRoleInBranchRoleMutation } = useDeleteRoleInBranchRoleMutation();

  useEffect(() => {
    if (isError) {
      console.error(errorData);
    }
  }, [isError, errorData]);

  useEffect(() => {
    filtersForm.setFieldsValue({
      [EFiltersForm.Counterparty]: loaderData.defaultCounterparty,
      [EFiltersForm.Branch]: loaderData.defaultBranch,
    });
  }, []);

	useEffect(() => {
    filtersForm.setFieldsValue({
      [EFiltersForm.Branch]: (branchesData as TCounterpartyAvailableBranchesResData)?.branches?.[0].guid,
    });
	}, [branchesData]);

  useEffect(() => {
    const pageAction = searchParams.get(EUserManagementBranchRolesPageGetParams.Action);

    if (pageAction === EUserManagementBranchRolesPageGetParamsActionTypes.Create) {
      setModalData({
	      counterpartiesData,
	      branchesData,
	      selectedCounterparty: filtersForm.getFieldValue(EFiltersForm.Counterparty),
	      selectedBranch: filtersForm.getFieldValue(EFiltersForm.Branch),
			});
    }
  }, [document.location.search]);

  const counterpartyOptions = (counterpartiesData?.counterparties || []).map((counterparty) => ({
    label: counterparty.name,
    value: counterparty.guid,
  }));

	const branchesOptions = ((branchesData as TCounterpartyAvailableBranchesResData)?.branches || []).map((branch) => ({
    label: branch.name,
    value: branch.guid,
  }));

  const sortPermissionTableData = (data: TPermissionTableResData | {}): TPermissionTableResData | {} => {
    const sorter = (a: TPermission | TRow, b: TPermission | TRow): number => a.sort_number - b.sort_number;
    ((data as TPermissionTableResData)?.rows || []).sort(sorter).forEach((row) => (row.permissions || []).sort(sorter));
    return data;
  };

  const sortedPermissionTableData = sortPermissionTableData(permissionTableData as TRolesListResData);

  const buildPermissionTableRows = (data: TPermissionTableResData | {}): TScopeRecord[] => {
    const { rows, roles } = data as TPermissionTableResData;

    const scopes: TScopeRecord[] = (rows || []).map((row) => {
      const generateUniqKey = row.permissions?.reduce((acc, item) => `${acc}#${item.guid}`, '');

      return {
        key: `${row.group_name}#${generateUniqKey}`,
        title: row.group_name,
        children: (row.permissions || []).map((permission) => ({
          key: permission.guid,
          scopeKey: row.group_name,
          title: permission.name,
          roles: (roles || []).reduce((acc, roleName) => {
            const rolePermission = (permission.roles || []).find((role_) => role_.name === roleName);
            return {
              [roleName]: !!rolePermission?.allowed,
              ...acc,
            };
          }, {}),
        })),
      };
    });

    return scopes;
  };

  const buildPermissionTableColumns = (data: TPermissionTableResData | {}): TTableColumn[] =>
    ((data as TPermissionTableResData).roles || []).map((roleName): { key: string, title: string } => ({
      key: roleName,
      title: roleName,
    }));

  const tableRows = buildPermissionTableRows(sortedPermissionTableData || {});
  const tableColumns = buildPermissionTableColumns(sortedPermissionTableData || {});

  const setTableContainerRef = useCallback<RefCallback<Element>>((node: Element | null): void => {
    const tableContentNode = node?.querySelector('.ant-table-content');
    if (tableContentNode) {
      scrollableTableNodeRef.current = tableContentNode;
    }
  }, []);

  const formValuesChangeHandler: FormProps['onValuesChange'] = async (values): Promise<void> => {
    try {
      const payload: TPayload = Object.entries(values).map(([key, value]) => {
        if (typeof value === 'boolean') {
          setField({
            key,
            value,
          });
        }

        const state = values[key];
        const [columnKey, , permissionKey] = key.split(KEY_SEPARATOR);
        const column = tableColumns.find((c) => c.key === columnKey)!;

        return {
          roleName: column.title,
          permissionGuid: permissionKey,
          state,
        };
      });

      await Promise.all(payload.map(async (item): Promise<void> => {
        const { roleName, permissionGuid, state } = item;

        const reqParams = {
          role_name: roleName,
          permission_guid: permissionGuid,
          branch_guid: filtersForm.getFieldValue(EFiltersForm.Branch),
        };
        if (state) {
          await addPermissionToBranchRoleMutation({
            counterpartyGUID: selectedCounterparty,
            requestData: reqParams,
          });
        } else {
          await removePermissionToBranchRoleMutation({
            counterpartyGUID: selectedCounterparty,
            requestData: reqParams,
          });
        }
      }));

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.BranchQueryKeys].getPermissionTableByBranch._def,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    permissionTableForm.setFieldValue(field?.key, field?.value);
  }, [formValuesChangeHandler]);

  const onColumnMenuItemClickHandler: TPermissionTableProps['onColumnMenuItemClick'] =
    async (values): Promise<void> => {
      try {
        const { key, column } = values;

        if (key === ERoleMenuItemKey.Delete) {
          const roleName = column.key;

          await deleteRoleInBranchRoleMutation({
            counterpartyGUID: selectedCounterparty,
            requestData: {
              role_name: roleName,
            },
          });

          await queryClient.invalidateQueries({
            queryKey: queryKeysClient[EQueryKeys.BranchQueryKeys].getPermissionTableByBranch._def,
          });
        }
      } catch (error) {
        console.error(error);
      }
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCH_ROLES);
  };

 const onFiltersFormValuesChangeHandler = (values: Record<EFiltersForm, string>): void => {
   const { counterparty } = values;

   if (counterparty) {
     setSelectedCounterparty(counterparty);
   }
 };

	const submitFiltersFormHandler = (values: Record<EFiltersForm, string>): void => {
		const { counterparty, branch } = values;

    setPermissionTableFilters({
      counterparty,
      branch,
    });
	};

  return (
    <SContainer ref={setTableContainerRef}>
      <SControlsContainer>
	      <Form
		      form={filtersForm}
		      onFinish={submitFiltersFormHandler}
          onValuesChange={onFiltersFormValuesChangeHandler}
	      >
		      <SInnerFiltersFormWrapper>
            <Form.Item
              name={EFiltersForm.Counterparty}
              hidden={IS_BANK_FE}
            >
              <Select
                style={{
                  width: 272,
                }}
                options={counterpartyOptions}
              />
            </Form.Item>
			      <Form.Item
              name={EFiltersForm.Branch}
            >
				      <Select
					      style={{ width: '280px' }}
					      placeholder={t('usrRoleMng.branchRoles.filterButtonBranch.placeholder') as string}
					      options={branchesOptions}
                loading={isBranchesDataLoading}
				      />
			      </Form.Item>
			      <Button
				      loading={isPermissionTableDataLoading}
              disabled={!branchesOptions.length}
				      htmlType="submit"
				      type="primary"
			      >
				      {t('usrRoleMng.branchRoles.searchButton.title')}
			      </Button>
		      </SInnerFiltersFormWrapper>
	      </Form>
        <SScrollNavWrapper>
          {scrollableTableNodeRef.current && <ScrollerComponent delta={200} node={scrollableTableNodeRef.current} />}
        </SScrollNavWrapper>
      </SControlsContainer>
      {branchesOptions.length ? (
        <PermissionTable
          onValuesChange={formValuesChangeHandler}
          form={permissionTableForm}
          widths={{
            role: 146,
            permission: 276,
          }}
          onColumnMenuItemClick={onColumnMenuItemClickHandler}
          loading={isPermissionTableDataLoading}
          rows={tableRows}
          columns={tableColumns}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
      <CreateRoleModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData}
      />
    </SContainer>
  );
};

export const loader = async (): Promise<{
  counterpartiesData: TCounterpartiesListReqParams | {},
  branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData | {},
  permissionTableData: TPermissionTableResData | {},
  defaultBranch: string,
  hasAccessForAllBranches: boolean,
  defaultCounterparty: string,
}> => {
  try {
    let counterpartiesData: TCounterpartiesListReqParams = {};

    if (IS_ADMIN_FE) {
      const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
      counterpartiesData =
        queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
        (await queryClient.fetchQuery(counterpartiesDataQuery));
    }

    const queryScopeListData =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(queryScopeListData.queryKey) ??
      (await queryClient.fetchQuery(queryScopeListData));

    const hasAccessForAllBranches =
      (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed || false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    const defaultBranchGUID = hasAccessForAllBranches
      ? (branchesData as TCounterpartyBranchesResData).list[0].guid
      : (branchesData as TCounterpartyAvailableBranchesResData).branches[0].guid;

    const permissionTableQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getPermissionTableByBranch(
      ENV_COUNTERPARTY_GUID,
      defaultBranchGUID,
      {},
    );

    const permissionTableData: TPermissionTableResData =
      queryClient.getQueryData(permissionTableQuery.queryKey) ??
      (await queryClient.fetchQuery(permissionTableQuery));

    return {
      permissionTableData,
      branchesData,
      counterpartiesData,
      hasAccessForAllBranches,
      defaultCounterparty: ENV_COUNTERPARTY_GUID,
      defaultBranch: defaultBranchGUID,
    };
  } catch (error) {
    console.error(error);

    return {
      permissionTableData: {},
      branchesData: {},
      counterpartiesData: {},
      hasAccessForAllBranches: false,
      defaultCounterparty: '',
      defaultBranch: '',
    };
  }
};

export default UserRoleManagementBranchRolesPage;
